import i from 'i18next'
import Link from 'next/link'
import { AnimatePresence, motion } from 'framer-motion'
import slugify from 'slugify'
import unique from 'array-unique'
import { currency } from 'lib/currency'
import { ProductThumbnail } from 'lib/hooks/use-products'
import Image from 'components/Image'
import { products as ease } from 'constants/easings'
import { useContext, useState } from 'react'
import Portal from 'components/Portal'
import QuickView from './QuickView'
import { State } from 'components/Layout'
import useMedia from 'use-media'

export const isPerfumeOption = ( o ) => (
	o.name === 'Perfume' ||
	/geur/i.test( o.name )
)

type Props = {
	product: ProductThumbnail
	collection?: string
	perfume?: string
	quickView?: boolean
	quickAdd?: boolean
}

export default function Thumbnail({
	product,
	collection = '',
	perfume = '',
	quickView,
	quickAdd,
}: Props ) {
	const { handle, title, images, rating, numReviews } = product
	const prices = unique( product.prices.map( parseFloat ) )
	const mobile = useMedia({ maxWidth: '900px' })

	collection ||= product.productType?.toLowerCase() || 'all'

	const variant = perfume ? product.variants.find( v => (
		v.selectedOptions.find( o => (
			isPerfumeOption( o ) &&
			o.value === perfume
		) )
	) ) : product.variants[ 0 ]

	const perfumes = product.options.find( isPerfumeOption )
	const numPerfumes = perfumes?.values?.length || 0

	const [ open, setOpen ] = useState( false )
	const openQuickView = e => {
		e.preventDefault()
		setOpen( true )
	}

	const { addToCart } = useContext( State )
	const handleAddToCart = e => {
		e.preventDefault()
		addToCart( variant )
	}

	return (
		<>
			<AnimatePresence>
				{open && (
					<Portal selector="#__next">
						<QuickView
							product={{
								...product,
								// Filter out images that are variant images
								images: images?.filter( i => !product.variants.find( v => (
									v.image.originalSrc === i.originalSrc
								) ) ),
							}}
							reviews={numReviews}
							rating={rating}
							onClose={() => setOpen( false )}
							secondaryButton={(
								<Link
									legacyBehavior
									href="/products/[collection]/[...product]"
									as={(
										`/products/${collection}/${handle}` +
										( perfume && `/${slugify( perfume, { lower: true })}` )
									)}
									scroll={false}
								>
									<a className="secondary button">
										{i.t( 'collection.quickview.more' )}
									</a>
								</Link>
							)}
						/>
					</Portal>
				)}
			</AnimatePresence>
			<Link
				legacyBehavior
				href="/products/[collection]/[...product]"
				as={(
					`/products/${collection}/${handle}` +
					( perfume && `/${slugify( perfume, { lower: true })}` )
				)}
				scroll={false}
			>
				<a
					aria-label={product.title}
					onClick={quickView ? openQuickView : undefined}
				>
					<motion.article
						whileHover={mobile ? undefined : 'hover'}
						variants={{
							initial: { opacity: 0, y: 50 },
							animate: { opacity: 1, y: 0 },
						}}
						transition={{
							opacity: { duration: 1, ease },
							default: { duration: 1, ease },
						}}
					>
						{product.metafields[ 'tag-right-nl' ] ? (
							<Label right>{product.metafields[ 'tag-right-nl' ]}</Label>
						) : product.tags?.includes( 'new' ) ? (
							<Label>{i.t( 'product.new' )}</Label>
						) : product.tags?.includes( 'gift' ) ? (
							<Label>{i.t( 'product.gift' )}</Label>
						) : product.tags?.includes( 'bestseller' ) && collection !== 'bestsellers' ? (
							<Label>{i.t( 'product.bestseller' )}</Label>
						) : null}
						{/* {!!rating && <Rating rating={rating} count={numReviews} />} */}
						<picture>
							<Image
								className="product"
								src={variant.image?.originalSrc}
								alt={variant.image?.altText}
								loading="lazy"
								sizes="
									(max-width: 400px) 90vw,
									(max-width: 900px) 45vw,
									(max-width: 1500px) 28.5vw,
									21.5vw
								"
								variants={{
									hover: { scale: 1.05 },
								}}
							/>
							<motion.div
								className="cta"
								initial={{ opacity: 0 }}
								variants={{
									hover: {
										opacity: 1,
										transition: { delay: 0.2 },
									},
								}}
							>
								{quickView ? (
									i.t( 'collection.quickview' )
								) : quickAdd ? (
									variant.availableForSale ? (
										<span onClick={handleAddToCart}>
											{i.t( 'collection.quickadd' )}
										</span>
									) : i.t( 'product.button.notinstockshort' )
								) : null}
								<div className="underline" />
							</motion.div>
						</picture>
						<h3>{title} {perfume}</h3>
						<p>
							{( !perfume && numPerfumes > 1 ) && (
								i.t( 'collection.perfumes', { count: numPerfumes }) +
								( prices.length > 0 ? ' — ' : '' )
							)}
							{prices.length > 0 && i.t(
								`collection.price${prices.length > 1 ? '.from' : ''}`,
								{ price: currency.format( Math.min( ...prices ) ) }
							)}
							{!!product.fromPrice && (
								<span className="from">
									{currency.format( product.fromPrice )}
								</span>
							)}
						</p>
					</motion.article>
					<style jsx>{`
						a :global( article ) {
							position: relative;
							flex: 1;
							text-align: left;
						}
						a :global( .rating ) {
							position: absolute;
							z-index: 1;
							left: 8%;
							top: 5%;
							transition: opacity .3s;
						}
						a :global( .rating .count ),
						a :global( .rating .count-short ) {
							margin-top: -1px;
						}
						@media ( min-width: 800px ) {
							a :global( .rating ) {
								opacity: 0;
							}
							a :global( article:hover .rating ) {
								opacity: 1;
								transition-delay: .05s;
							}
						}
						picture {
							position: relative;
							height: 40vw;
							padding: 30% 0%;
							margin-bottom: 25px;
							background-color: #f7f7f7;
						}
						picture :global( img.product ) {
							object-fit: contain;
							image-rendering: -webkit-optimize-contrast;
						}
						picture :global( img.hover ) {
							position: absolute;
							top: 0;
							bottom: 0;
							left: 0;
							right: 0;
							opacity: 0;
							transition: opacity ease-in-out .5s;
						}
						@media ( min-width: 700px ) {
							picture:hover :global( img.hover ) {
								opacity: 1;
							}
						}
						picture :global( .cta ) {
							position: absolute;
							bottom: calc( 16% - 40px );
							left: 50%;
							transform: translateX(-50%);
							text-align: center;
							text-transform: lowercase;
						}
						picture :global( .cta .underline ) {
							position: absolute;
							bottom: -4px;
							left: 1px;
							right: 1px;
							height: 1px;
							background-color: currentColor;
						}
						h3,
						p {
							margin: 0;
							font-size: 20px;
							font-weight: normal;
							line-height: 1.45;
							text-transform: lowercase;
						}
						a {
							text-decoration: none;
						}
						.from {
							position: relative;
							margin-left: .5em;
							color: #B8B8B8;
						}
						.from::after {
							content: '';
							border-bottom: 1px solid currentColor;
							position: absolute;
							left: 0;
							top: 52%;
							width: 100%;
						}
						@media ( max-width: 900px ) {
							h3,
							p {
								font-size: 20px;
							}
							picture {
								padding: 10% 0;
							}
							picture :global( .cta ) {
								padding: 20px;
								bottom: calc( 15% - 40px );
								white-space: nowrap;
								opacity: 1 !important;
							}
							picture :global( .cta .underline ) {
								bottom: 16px;
								left: 21px;
								right: 21px;
							}
						}
					`}</style>
				</a>
			</Link>
		</>
	)
}

const Label = ({ children, right = false }) => (
	<div>
		{children}
		<style jsx>{`
			div {
				background-color: white;
				width: max-content;
				position: absolute;
				top: 24px;
				${!right ? 'left': 'right'}: 24px;
				z-index: 1;
				padding-inline: 8px;
				text-transform: lowercase;
			}
		`}</style>
	</div>
)
